// packages/carbon/src/no-code/pulse/scopeRegistry.ts
import { clearNameProvider, clearSnapshotProvider, pulse, setSnapshotProvider } from './floor';
import { isPulseEnabled } from './runtime';
import type { PulseScope, ScopeId } from './types';

const scopes = new Map<ScopeId, PulseScope>(); // always-populated source of truth (ungated)
// stable scope ids that replace the old minted m1/m2. the app store is a singleton, so it gets one
// const id; a page store identifies by its interfacePageId, using the fallback only in the runner
// (where pulse is off, so the value is inert).
export const APP_MANAGER_ID = 'app';
export const PAGE_MANAGER_FALLBACK = 'page';
// sentinel scope for an emit site that couldn't resolve a real store manager (a provider-less mount).
// emit sites skip these so an unattributable event never pollutes the stream. `base` is an internal
// grammar token — never surface it in user-facing labels.
export const UNKNOWN_MANAGER_ID = 'unknown';
export const UNKNOWN_SCOPE = 'unknown:base';
export function scopeIdOf(managerId: string, instanceId: string | null): ScopeId {
  return `${managerId}:${instanceId ?? 'base'}`;
}
// The app store is a singleton, so its scope id is one constant derived here and shared by the store
// tagging (BaseGlobalAppStateStoreProvider) and the builder-only registration (AppPulseScopeRegistrar).
export const APP_SCOPE_ID = scopeIdOf(APP_MANAGER_ID, null);
function mergeScope(existing: PulseScope, newScope: PulseScope): PulseScope {
  const result = { ...existing };
  for (const key of Object.keys(newScope) as (keyof PulseScope)[]) {
    if (newScope[key] !== undefined) (result as Record<string, unknown>)[key] = newScope[key];
  }
  return result;
}

export function registerScope(scope: PulseScope, snapshotProvider?: () => unknown): void {
  // Zero-overhead pulse: the runner never enables pulse, so registration is a no-op there — no Map
  // writes, no retained snapshot closures. In the builder, enablePulseForBuilder() runs (in
  // InterfaceAppProviders render) before any provider effect, so this is already true by first mount.
  if (!isPulseEnabled()) return;
  const existing = scopes.get(scope.scopeId);
  const merged = existing ? mergeScope(existing, scope) : scope;
  scopes.set(merged.scopeId, merged);
  // ungated so the provider survives an off→on toggle (pm-001); pulse.registerScope stays gated
  if (snapshotProvider) setSnapshotProvider(merged.scopeId, snapshotProvider);
  // parentScopeId still rides along on the scope object for the breadcrumb (formatScopeParts);
  // name/icon resolution no longer uses it — floor scans across all scopes instead.
  pulse.registerScope(merged); /* posts to store only when on */
}
export function unregisterScope(id: ScopeId): void {
  if (!isPulseEnabled()) return;
  scopes.delete(id);
  clearSnapshotProvider(id);
  // NOTE: name/icon providers are deliberately NOT cleared here. They are owned solely by
  // registerScopeDisplayProviders (Page/AppPulseEntityDisplayRegistrar), which has its own independent mount
  // lifecycle and clears them on its own teardown. Those registrars' remounts (HMR / StrictMode /
  // store re-provision) fire out of sync with this one — clearing the name provider here wiped a
  // provider this module never set, leaving page-scope blocks permanently unresolved (raw ids).
  pulse.unregisterScope(id);
}
export function listScopes(): PulseScope[] {
  return [...scopes.values()];
}

export function buildScopeMap(): Map<ScopeId, PulseScope> {
  return new Map(scopes);
}
export const __resetForTest = () => {
  // also drop the floor-side providers this module registered, else a recycled
  // ScopeId in a later test inherits a closure over a prior test's destroyed store.
  for (const id of scopes.keys()) {
    clearSnapshotProvider(id);
    clearNameProvider(id);
  }
  scopes.clear();
};
